import {
	ArrowLeft02Icon,
	Delete02Icon,
	Download01Icon,
	FileDownloadIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCard } from "@/components/check-card";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import type { Country } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import {
	type EditingPerson,
	EditingPersonItem,
} from "@/components/editing-person-item";
import {
	PERSON_FIELD_LABELS,
	type PersonFormData,
	PersonFormFields,
} from "@/components/person-form-fields";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field, FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { VerificationCard } from "@/components/verification-card";
import type ClientsIndividualEntrepreneurClient from "@/types/serializers/ClientsIndividualEntrepreneurClient";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	client_id: number;
	individual_entrepreneur_client: ClientsIndividualEntrepreneurClient;
	countries: Country[];
}

interface FormData {
	ico: string;
	physical_person: PersonFormData;
}

export default function IndividualEntrepreneurClientShow({
	user,
	client_id,
	individual_entrepreneur_client,
	countries,
}: Props) {
	const [editingPerson, setEditingPerson] = useState<EditingPerson>();
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: PersonFormData;
		description: string;
	}>();

	const { data, setData, patch, processing, errors, clearErrors, transform } =
		useForm<FormData>({
			ico: individual_entrepreneur_client.ico,
			physical_person: {
				first_name: individual_entrepreneur_client.first_name,
				last_name: individual_entrepreneur_client.last_name,
				birth_number: individual_entrepreneur_client.birth_number ?? "",
				street: individual_entrepreneur_client.street,
				city: individual_entrepreneur_client.city,
				postal_code: individual_entrepreneur_client.postal_code,
				country: individual_entrepreneur_client.country,
				document_type: individual_entrepreneur_client.document_type,
				document_number: individual_entrepreneur_client.document_number,
				document_expiry_date:
					individual_entrepreneur_client.document_expiry_date,
				document_issuer: individual_entrepreneur_client.document_issuer,
				birth_date: individual_entrepreneur_client.birth_date,
				birth_place: individual_entrepreneur_client.birth_place,
				birth_country: individual_entrepreneur_client.birth_country,
				citizenship: individual_entrepreneur_client.citizenship,
				citizenship_other: individual_entrepreneur_client.citizenship_other,
				gender: individual_entrepreneur_client.gender,
				is_sanctioned: individual_entrepreneur_client.is_sanctioned,
				is_pep: individual_entrepreneur_client.is_pep,
				pep_info: individual_entrepreneur_client.pep_info,
			},
		});

	const aresMutation = useMutation({
		mutationFn: async (ico: string) => {
			const response = await fetch(`/ares/lookup_rzp?ico=${ico}`);
			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Nepodařilo se načíst data z ARES");
			}
			return response.json();
		},
		onSuccess: (result) => {
			const entity = result.entity;
			applyOrShowConflicts(
				{
					...entity,
					birth_number: "",
					document_type: null,
					document_number: null,
					document_expiry_date: null,
					document_issuer: null,
				},
				"Údaje byly načteny z ARES.",
			);
		},
		onError: (error: Error) => {
			toast.error(error.message);
		},
	});

	const defaults: Partial<PersonFormData> = { country: "CZ" };

	const applyOrShowConflicts = (
		values: PersonFormData,
		description: string,
	) => {
		const currentValues = data.physical_person;
		const merged = mergeValues(currentValues, values);
		const conflicts = findConflicts(
			currentValues,
			values,
			PERSON_FIELD_LABELS,
			defaults,
		);
		if (conflicts.length === 0) {
			setData("physical_person", merged);
		} else {
			setPending({
				conflicts,
				mergedValues: merged,
				description,
			});
		}
	};

	const handleApply = () => {
		if (pending) setData("physical_person", pending.mergedValues);
		setPending(undefined);
	};

	const handleCancel = () => {
		setPending(undefined);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		transform((data) => ({
			ico: data.ico,
			physical_person: {
				...data.physical_person,
				id: editingPerson?.id,
			},
		}));
		patch(
			`/individual_entrepreneur_clients/${individual_entrepreneur_client.id}`,
		);
	};

	const handleDelete = () => {
		router.delete(`/clients/${client_id}`);
	};

	const warnings = {
		birth_number: individual_entrepreneur_client.missing_birth_number,
		address: individual_entrepreneur_client.missing_address,
		document: individual_entrepreneur_client.missing_document,
		document_expiring: individual_entrepreneur_client.document_expiring,
		document_expired: individual_entrepreneur_client.document_expired,
		birth_info: individual_entrepreneur_client.missing_birth_info,
		citizenship: individual_entrepreneur_client.missing_citizenship,
		gender: individual_entrepreneur_client.missing_gender,
		aml_check: individual_entrepreneur_client.missing_aml_check,
	};

	return (
		<>
			<Head title={individual_entrepreneur_client.full_name} />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div className="flex items-center justify-between">
								<div className="flex items-start gap-4">
									<Button
										type="button"
										variant="ghost"
										size="icon"
										render={<Link href="/clients" />}
									>
										<HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={2} />
									</Button>
									<div>
										<div className="flex items-center gap-3">
											<h2 className="font-semibold text-2xl">
												{individual_entrepreneur_client.full_name}
											</h2>
											<Badge variant="outline">OSVČ</Badge>
										</div>
									</div>
								</div>
								<div className="flex gap-2">
									<AlertDialog>
										<AlertDialogTrigger
											render={
												<Button type="button" variant="destructive">
													<HugeiconsIcon
														icon={Delete02Icon}
														strokeWidth={2}
														data-icon="inline-start"
													/>
													Smazat
												</Button>
											}
										/>
										<AlertDialogContent size="sm">
											<AlertDialogHeader>
												<AlertDialogMedia className="bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive">
													<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
												</AlertDialogMedia>
												<AlertDialogTitle>Smazat klienta?</AlertDialogTitle>
												<AlertDialogDescription>
													Tato akce je nevratná.
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel variant="outline">
													Zrušit
												</AlertDialogCancel>
												<AlertDialogAction
													variant="destructive"
													onClick={handleDelete}
												>
													Smazat
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<CheckCard
									checked={individual_entrepreneur_client.checked}
									checkedAt={individual_entrepreneur_client.checked_at}
									checkExpiresAt={
										individual_entrepreneur_client.check_expires_at
									}
									checkUrl={`/individual_entrepreneur_clients/${individual_entrepreneur_client.id}/check`}
									uncheckUrl={`/individual_entrepreneur_clients/${individual_entrepreneur_client.id}/uncheck`}
								/>

								<VerificationCard
									verified={individual_entrepreneur_client.verified}
									verifiedAt={individual_entrepreneur_client.verified_at}
									verificationExpiresAt={
										individual_entrepreneur_client.verification_expires_at
									}
									verifyUrl={`/individual_entrepreneur_clients/${individual_entrepreneur_client.id}/verify`}
									unverifyUrl={`/individual_entrepreneur_clients/${individual_entrepreneur_client.id}/unverify`}
								/>
							</div>

							<Card>
								<CardHeader>
									<CardTitle>
										IČO <span className="text-destructive">*</span>
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="flex gap-2">
										<Field className="flex-1" data-invalid={!!errors.ico}>
											<Input
												id="ico"
												value={data.ico}
												onChange={(e) => {
													clearErrors("ico");
													setData("ico", e.target.value);
												}}
												placeholder="12345678"
												aria-invalid={!!errors.ico}
											/>
											<FieldError>{errors.ico}</FieldError>
										</Field>
										<Button
											type="button"
											variant="outline"
											onClick={() => aresMutation.mutate(data.ico)}
											disabled={!data.ico || aresMutation.isPending}
										>
											<HugeiconsIcon
												icon={Download01Icon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											{aresMutation.isPending ? "Načítám..." : "Načíst z ARES"}
										</Button>
									</div>
								</CardContent>
							</Card>

							{editingPerson && (
								<EditingPersonItem
									person={editingPerson}
									onCancel={() => setEditingPerson(undefined)}
									variant="merge"
								/>
							)}

							<PersonFormFields
								value={data.physical_person}
								onChange={(person) => {
									setData("physical_person", person);
									clearErrors();
								}}
								errors={errors}
								countries={countries}
								onPersonSelected={(person) => {
									setEditingPerson(person);
									applyOrShowConflicts(
										person.formData,
										`Vybrali jste existující osobu ${person.formData.first_name} ${person.formData.last_name}.`,
									);
								}}
								warnings={warnings}
							/>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancel}
								/>
							)}
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl items-center justify-between">
							<DropdownMenu>
								<DropdownMenuTrigger
									render={
										<Button type="button" variant="outline">
											<HugeiconsIcon
												icon={FileDownloadIcon}
												strokeWidth={2}
												data-icon="inline-start"
											/>
											Formuláře
										</Button>
									}
								/>
								<DropdownMenuContent align="start" side="top">
									<DropdownMenuItem
										render={
											<a
												href={`/identification_pdfs/individual_entrepreneur/${individual_entrepreneur_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Záznam o identifikaci
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/client_control_pdfs/individual_entrepreneur/${individual_entrepreneur_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Záznam o kontrole klienta
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/cooperation_refusal_pdfs/individual_entrepreneur/${individual_entrepreneur_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Odmítnutí spolupráce
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/discrepancy_notice_pdfs/individual_entrepreneur/${individual_entrepreneur_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Upozornění na nesrovnalost
									</DropdownMenuItem>
									<DropdownMenuItem
										render={
											<a
												href={`/suspicious_transaction_pdfs/individual_entrepreneur/${individual_entrepreneur_client.id}`}
											/>
										}
									>
										<HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
										Podezřelá transakce
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
							<div className="flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/clients" />}
								>
									Zpět
								</Button>
								<Button type="submit" size="lg" disabled={processing}>
									{editingPerson ? "Sloučit osoby" : "Uložit změny"}
								</Button>
							</div>
						</div>
					</div>
				</form>
			</Dashboard>
		</>
	);
}
